import '../../motion.css';
import './build.css';
import './building.css';
import type { ReactNode } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { buildExperienceCopy } from '@/i18n/ns/build-experience';
import { FieldView, PendingShape, StreamCaret, StreamList } from './stream';
import type {
  CanvasVM,
  CompleteFragment,
  Decimal,
  DisplayFragment,
  PendingFragment,
  RegisteredKind,
} from './vm';

/** How a kind lays out while it builds. Content inside each fragment is the viewer's. */
export type BuildingLayout = 'sections' | 'slides' | 'pages' | 'grid' | 'screen';

export const LAYOUT: Record<RegisteredKind, BuildingLayout> = {
  report: 'sections',
  dashboard: 'sections',
  chart: 'sections',
  page: 'sections',
  site: 'sections',
  deck: 'slides',
  document: 'pages',
  spreadsheet: 'grid',
  table: 'grid',
  app: 'screen',
  markdown: 'pages',
  kpi: 'sections',
};

/**
 * A plain display of a field value for the generic pending view. A decimal shows its
 * canonical string and unit as stored; locale formatting is the viewer's (UI1), and a decimal
 * is never parsed into a JS number here.
 */
export function plainValue(value: string | Decimal | boolean): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? '✓' : '—';
  const unit = value.currency ?? value.unit;
  return unit ? `${value.value} ${unit}` : value.value;
}

/**
 * A pending fragment drawn honestly: the text written so far, a pending shape for every field
 * not there yet, a dash for an unavailable one. No field becomes 0 or invented content.
 * `writing` marks the one fragment being written now; only its last ready text carries the caret.
 */
export function PendingFragmentView({
  fragment,
  writing = false,
}: {
  fragment: PendingFragment;
  writing?: boolean;
}) {
  const entries = Object.entries(fragment.fields);
  if (!entries.length) return <PendingShape shape="block" />;
  const lastText = !writing
    ? -1
    : entries.reduce<number>(
        (last, [, field], i) =>
          field.status === 'ready' && typeof field.value === 'string' ? i : last,
        -1,
      );
  return (
    <div className="pc-building-pending" data-node-type={fragment.nodeType}>
      {entries.map(([name, field], i) =>
        i === lastText && field.status === 'ready' ? (
          <p key={name} data-field={name}>
            <StreamCaret text={String(field.value)} done={false} />
          </p>
        ) : (
          <p key={name} data-field={name}>
            <FieldView field={field} format={plainValue} />
          </p>
        ),
      )}
    </div>
  );
}

/**
 * The app canvas's display node types while an app builds (NOTES-v3 moments a and b),
 * acknowledged by T1-ART as canvas placeholders only (not an App payload schema, not
 * inspection coverage). Fields are ART's canonical Field envelopes; a list row shows only its
 * declared display fields. Unknown node types use the generic pending view.
 */
export const APP_NODE_TYPES = ['kpi', 'collection', 'form-field', 'list-row', 'section'] as const;

const readyText = (f: PendingFragment, name: string): string | null => {
  const field = f.fields[name];
  return field && field.status === 'ready' ? plainValue(field.value) : null;
};

/**
 * A named field of an app node as ART projects it: ready text, a pending shape, or the canonical
 * unavailable dash. A field the node has not declared yet is a pending shape; an unavailable one
 * never reads as still coming.
 */
const NodeText = ({ f, name }: { f: PendingFragment; name: string }) => {
  const field = f.fields[name];
  return field ? (
    <FieldView field={field} format={plainValue} shape="inline" />
  ) : (
    <PendingShape shape="inline" />
  );
};

/** One app node while it builds: a KPI, a schema chip, a form field, a landing row. */
export function AppPendingNode({
  fragment,
  writing,
}: {
  fragment: PendingFragment;
  writing: boolean;
}) {
  const copy = useNamespaceCopy(buildExperienceCopy);
  if (fragment.nodeType === 'collection') {
    const count = readyText(fragment, 'fieldCount');
    return (
      <span className="pc-app-chip" data-node-type="collection">
        <NodeText f={fragment} name="name" />
        {count && (
          <span className="pc-build-meta"> · {formatCopy(copy.fieldsCount, { n: count })}</span>
        )}
      </span>
    );
  }
  if (fragment.nodeType === 'form-field') {
    return (
      <label className="pc-app-field" data-node-type="form-field">
        <span>
          <NodeText f={fragment} name="label" />
        </span>
        <span className="pc-app-field-box" aria-hidden="true" />
      </label>
    );
  }
  if (fragment.nodeType === 'kpi') {
    const value = fragment.fields.value;
    return (
      <div className="pc-app-kpi" data-node-type="kpi">
        <span className="pc-build-meta">
          <NodeText f={fragment} name="label" />
        </span>
        <span className="pc-app-kpi-value">
          {value ? (
            <FieldView field={value} format={plainValue} shape="value" />
          ) : (
            <PendingShape shape="value" />
          )}
        </span>
      </div>
    );
  }
  if (fragment.nodeType === 'list-row') {
    const cells = Object.entries(fragment.fields);
    return (
      <div className="pc-app-row" data-node-type="list-row">
        {cells.map(([name, field]) => (
          <span key={name} data-field={name}>
            <FieldView field={field} format={plainValue} />
          </span>
        ))}
      </div>
    );
  }
  return <PendingFragmentView fragment={fragment} writing={writing} />;
}

/**
 * The canvas while an Artifact builds, for every kind. Fragments arrive in order and keep
 * their C2 node ids from pending to complete, so nothing jumps when one completes.
 *
 * `renderComplete` is the viewer's own renderer for a finished node (UI1); there is no
 * second renderer here. `renderPending` lets a viewer draw its own pending variant; the
 * default is PendingFragmentView.
 */
export function BuildingCanvas({
  vm,
  renderComplete,
  renderPending,
}: {
  vm: CanvasVM;
  renderComplete: (fragment: CompleteFragment) => ReactNode;
  renderPending?: (fragment: PendingFragment, writing: boolean) => ReactNode;
}) {
  if (vm.state.status !== 'ready') return null;
  const { kind, fragments } = vm.state.data;
  const layout = LAYOUT[kind];
  // The fragment being written is the last one still pending that has text to show: the one
  // caret on the canvas sits at its end. A pending table or chart waits as a shape.
  const writingId =
    vm.state.data.stage === 'complete'
      ? null
      : ([...fragments]
          .reverse()
          .find(
            (f) =>
              f.kind === 'pending-fragment' &&
              Object.values(f.fields).some(
                (v) => v.status === 'ready' && typeof v.value === 'string',
              ),
          )?.nodeId ?? null);
  const render = (fragment: DisplayFragment) =>
    fragment.kind === 'complete-fragment'
      ? renderComplete(fragment)
      : (renderPending?.(fragment, fragment.nodeId === writingId) ??
        (layout === 'screen' ? (
          <AppPendingNode fragment={fragment} writing={fragment.nodeId === writingId} />
        ) : (
          <PendingFragmentView fragment={fragment} writing={fragment.nodeId === writingId} />
        )));
  const list = (
    <StreamList
      items={fragments}
      enterKey={vm.scope.jobId ?? `${vm.scope.artifactId ?? ''}:${vm.scope.revisionId ?? ''}`}
      id={(f) => f.nodeId}
      render={(f) => (
        <div
          className="pc-building-node"
          data-state={f.kind === 'complete-fragment' ? 'complete' : 'pending'}
          data-node-type={f.kind === 'pending-fragment' ? f.nodeType : undefined}
        >
          {render(f)}
        </div>
      )}
      flow={layout === 'slides' ? 'row' : 'column'}
      landing={
        layout === 'grid'
          ? true
          : layout === 'screen'
            ? (f: DisplayFragment) => f.kind === 'pending-fragment' && f.nodeType === 'list-row'
            : false
      }
    />
  );
  return (
    <div
      className="pc-building"
      data-layout={layout}
      data-kind={kind}
      data-testid="building-canvas"
    >
      {layout === 'pages' ? <div className="pc-building-page">{list}</div> : list}
    </div>
  );
}
